import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import TextField from "@mui/material/TextField";
import { usePromptOnLeave } from '../../hooks/usePromptOnLeave.js';
import {
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';

import style from "./CompanyForm.module.css";

const CompanyForm = ({ initialCompanyName, initialCompanySlug, onSave, onReset }) => {
  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState(initialCompanyName);
  const [companySlug, setCompanySlug] = useState(initialCompanySlug);
  const [errors, setErrors] = useState({});
  const [isModified, setIsModified] = useState(false);

  const slugPattern = /^[a-zA-Z0-9_-]+$/;

  useEffect(() => {
    setCompanyName(initialCompanyName);
    setCompanySlug(initialCompanySlug);
    setErrors({});
    setIsModified(false);
  }, [initialCompanyName, initialCompanySlug]);

  const validateField = (field, value) => {
    switch (field) {
      case "companyName":
        if (!value.trim()) {
          return t('common.required');
        }
        break;
      case "companySlug":
        if (!value.trim()) {
          return t('common.required');
        } else if (!slugPattern.test(value)) {
          return t('common.slug_hint');
        }
        break;
      default:
        return "";
    }
    return "";
  };

  const handleChange = (field, value) => {
    if (field === "companyName") {
      setCompanyName(value);
    } else if (field === "companySlug") {
      setCompanySlug(value);
    }

    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));

    const modified =
      value !== (field === "companyName" ? initialCompanyName : initialCompanySlug) ||
      companyName !== initialCompanyName ||
      companySlug !== initialCompanySlug;
    setIsModified(modified);
  };

  const isFormValid = () => {
    return (
      !validateField("companyName", companyName) &&
      !validateField("companySlug", companySlug)
    );
  };

  const handleSaveChanges = () => {
    if (!isFormValid()) return;

    onSave(companyName, companySlug);
    setIsModified(false);
    setErrors({});
  };

  const handleResetChanges = () => {
    setCompanyName(initialCompanyName);
    setCompanySlug(initialCompanySlug);
    setErrors({});
    setIsModified(false);
    onReset();
  };

  const isSaveEnabled = isModified && isFormValid();

   const { showModal, onConfirm, onCancel } = usePromptOnLeave(
    t('common.unsaved_changes'),
    isSaveEnabled
  );

  return (
    <div className={style.container}>
      <h2 className="section-title">{t('company.section_title')}</h2>
      <p className={`grey-text ${style.subtitle}`}>
        {t('company.slug_hint')} <span>
        /board/<strong>{companySlug}</strong>/board-id</span>
      </p>
      <div className={style.form}>
        <TextField
          className={style.input}
          variant="outlined"
          fullWidth
          value={companyName}
          required
          label={t('company.name_label')}
          placeholder={t('company.name_label')}
          margin="normal"
          error={!!errors.companyName}
          helperText={errors.companyName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          onChange={(e) => handleChange("companyName", e.target.value)}
        />
        <TextField
          variant="outlined"
          fullWidth
          value={companySlug}
          required
          label={t('company.slug_label')}
          placeholder={t('company.slug_label')}
          error={!!errors.companySlug}
          helperText={errors.companySlug}
          margin="normal"
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          onChange={(e) => handleChange("companySlug", e.target.value)}
        />
      </div>
      <div className={style.buttonsContainer}>
        <button
          onClick={handleResetChanges}
          disabled={!isModified}
          className={`button thirdly-button ${style.cancelButton}`}
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSaveChanges}
          disabled={!isSaveEnabled}
          className={`button ${style.saveButton}`}
        >
          {t('common.save')}
        </button>
      </div>
      <Dialog
        open={showModal}
        onClose={onCancel}
        aria-labelledby="unsaved-changes-title"
        aria-describedby="unsaved-changes-description"
        PaperProps={{ className: "dialogPaper" }}
      >
        <DialogContent>
          <p sx={{
            padding: "30px 25px",
            fontFamily: "Inerta",
          }}>
            {t('common.unsaved_changes')}
          </p>
        </DialogContent>
        <DialogActions sx={{padding: "20px"}}>
          <button onClick={onCancel} className="button secondary-button cancelButton">
            {t('common.cancel_short')}
          </button>
          <button onClick={onConfirm} className="button primary-button saveButton">
            {t('common.leave')}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CompanyForm;
